// KPI strip across the top of the Monitor view: strategy name + status badge + key
// rolling metrics (signals today, last fire, alert delivery success).

import type { AlertSummaryDto, StrategyDto } from "../../api/types";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Activity, Zap, Clock, CircleCheck, CircleX } from "lucide-react";
import { parseBackendTimestamp } from "../../hooks/time";
import { cn } from "@/lib/utils";

interface Props {
  strategy: StrategyDto;
  alerts: AlertSummaryDto[];
}

export function MonitorKpiStrip({ strategy, alerts }: Props) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayMs = today.getTime();
  const signalsToday = alerts.filter(
    (a) => parseBackendTimestamp(a.signalBarTime) >= todayMs,
  ).length;
  const lastFire = alerts.length > 0
    ? formatRelative(parseBackendTimestamp(alerts[0]!.signalBarTime))
    : "—";
  const delivered = alerts.filter((a) => a.deliveryStatus === "delivered").length;
  const failed = alerts.filter((a) => a.deliveryStatus === "failed" || a.deliveryStatus === "partial").length;

  const statusBadge =
    strategy.status === "active"
      ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-400"
      : strategy.status === "paused"
        ? "border-amber-500/40 bg-amber-500/10 text-amber-400"
        : "border-zinc-500/40 bg-zinc-500/10 text-zinc-400";

  return (
    <Card className="p-4 bg-card flex items-stretch gap-4 flex-wrap">
      <div className="flex-1 min-w-[260px]">
        <div className="flex items-center gap-2 flex-wrap">
          <h2 className="text-base font-semibold tracking-tight">{strategy.name}</h2>
          <Badge variant="outline" className={cn("border", statusBadge)}>
            {strategy.status}
          </Badge>
        </div>
        <div className="flex items-center gap-1.5 mt-1 text-xs">
          <Badge variant="outline" className="font-mono">{strategy.spec.coin}</Badge>
          <Badge variant="outline" className="font-mono">{strategy.spec.interval}</Badge>
          <Badge variant="outline" className="font-mono uppercase">{strategy.spec.marketType}</Badge>
          <code className="text-muted-foreground ml-1">{strategy.id.slice(0, 8)}…</code>
        </div>
      </div>

      <Kpi icon={<Zap className="size-3.5" />} label="Signals today" value={signalsToday} />
      <Kpi icon={<Clock className="size-3.5" />} label="Last fire" value={lastFire} />
      <Kpi icon={<CircleCheck className="size-3.5 text-emerald-500" />} label="Delivered" value={delivered} />
      <Kpi icon={<CircleX className="size-3.5 text-red-500" />} label="Failed / partial" value={failed} />
      <Kpi icon={<Activity className="size-3.5" />} label="Channels" value={strategy.channels.length} />
    </Card>
  );
}

function Kpi({ icon, label, value }: { icon: React.ReactNode; label: string; value: React.ReactNode }) {
  return (
    <div className="min-w-[110px] flex flex-col gap-0.5">
      <div className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-muted-foreground">
        {icon}
        {label}
      </div>
      <div className="font-mono text-base font-semibold">{value}</div>
    </div>
  );
}

function formatRelative(ms: number): string {
  if (!Number.isFinite(ms)) return "—";
  const delta = Date.now() - ms;
  if (delta < 0) return new Date(ms).toLocaleString();
  if (delta < 60_000) return "just now";
  if (delta < 3600_000) return `${Math.floor(delta / 60_000)}m ago`;
  if (delta < 86400_000) return `${Math.floor(delta / 3600_000)}h ago`;
  return `${Math.floor(delta / 86400_000)}d ago`;
}
